// Ring systems, from the occultation profile.
//
// One flat two-sided annulus per body with a `rings` block, in the body's own
// equatorial plane, with the light going both ways: the planet's shadow falls
// on the rings and the rings' shadow falls on the planet, both as a ray against
// a plane and a sphere rather than a shadow map
// (docs/roadmap/05-atmosphere-and-light.md, "Rings").
//
// The brightness is single scattering off a homogeneous slab, and it is TWO
// terms, not one texture: the lit face saturates with optical depth, so the B
// ring is bright there and the Cassini Division is not, while the unlit face is
// brightest where tau is about the view's own cosine, so the same Division and
// the C ring are what shows through and the B ring goes dark. A texture painted
// from one photograph is wrong on the other side, which is why there is no
// texture - only tau, from `tools/atlas/bake_rings.py`.

import * as THREE from 'three';

import { applyBodyShadow, bodyShadowKey, bodyShadowRefOf } from './bodyShadow.ts';

import type { BodyRow, RingComponent } from '../world/types';

/**
 * Drawn size a ring system has to reach before it is worth a draw.
 *
 * The same rule and the same number `atmoSky.ts` holds the scattering shell to:
 * 0.05 degrees is one pixel at `07-performance-and-instruments.md`'s reference
 * viewport. Read off the TRUE angular diameter and not the drawn one, for the
 * reason `sol.ts` gives its aureole - the drawn one is floored to keep a body
 * clickable (`MIN_DRAWN_ANGULAR_DEG`) and would never fall through this gate.
 *
 * Which is why the gate is a floor under a FADE and not a toggle: at 2.15 AU
 * from Saturn the disc is held at that floor and drawn about 7x its true size,
 * so an annulus whose true diameter has just fallen through this is still a
 * third of a degree on screen - `setFade` ramps it out over the decade above.
 */
export const RING_MIN_ANGULAR_DIAMETER_DEG = 0.05;
/** Where the fade above the gate is done: one decade of true angular size. */
export const RING_FULL_ANGULAR_DIAMETER_DEG = 0.5;

/**
 * Henyey-Greenstein asymmetry for the two terms.
 *
 * Negative on the reflected term because ring particles backscatter - the
 * opposition surge is the same effect - and positive on the transmitted one,
 * which is forward-scattered light coming through the low-tau gaps.
 */
const RING_G_BACK = -0.35;
const RING_G_FORWARD = 0.55;

/** Segments around the rim. At 384 the inner edge's sagitta is under a pixel. */
const RING_THETA_SEGMENTS = 384;

/** Widest row the profile is reshaped into: 73,320 texels is over any max width. */
const RING_TEX_MAX_WIDTH = 4096;

/** One texel per kilometre, which is what the bake writes. */
const RING_KM_PER_TEXEL = 1;

/**
 * Floors on the two cosines, shared with `atlas.ts`'s ring probe.
 *
 * Not guards against a divide: an exactly edge-on ring or an exactly
 * equinoctial sun make both of these zero and every term below infinite. An
 * instrument that predicts a brightness from the profile has to floor them the
 * same way or it is predicting a different shader.
 */
export const RING_MU_VIEW_FLOOR = 0.02;
export const RING_MU_SUN_FLOOR = 0.005;

/**
 * Taps across a pixel's own radial footprint.
 *
 * The brightness of a pixel is the MEAN over that pixel's footprint of the
 * single-scattering term, not the term of the mean tau: the reflected term
 * saturates, so a 35 km ring of tau 6.65 inside a 103 km pixel is a third of
 * an opaque ring and not a whole ring of tau 2.2. Averaging tau first is what
 * drew Uranus's epsilon at three times its covered fraction on the one pixel
 * row that caught it and nothing on the rest.
 *
 * The term is nonlinear in tau AND its exponent carries the fragment's own two
 * cosines, so no prefix sum over the profile can be built ahead of time; the
 * mean has to be taken over the term itself. One tap per profile texel where
 * the footprint is narrow enough for that, capped here, which is a stratified
 * sample of the footprint rather than an exact box sum once a pixel spans more
 * than this many kilometres.
 */
export const RING_FOOTPRINT_TAPS = 64;

/**
 * `tau_max` for a profile whose bake published no sidecar.
 *
 * The real one is per body, in `<the profile's stem>.meta.json`'s
 * `tau_encoding.tau_max` (`tools/atlas/bake_rings.py`), because one encoding
 * step is `tau_max/65535`: read at Saturn's 10.0, a system whose own ceiling
 * is 1e-3 quantises its whole signal to zero. This is what a row with no
 * sidecar at all falls back to, and every baked system has one.
 */
const RING_TAU_MAX_FALLBACK = 10.0;

/**
 * The bake's own `tau_max` for this profile, off its sidecar.
 *
 * A sidecar that is not there is the fallback above; a sidecar that IS there
 * and does not publish a positive ceiling is a refusal, because silently
 * decoding at another body's ceiling is the fault this read exists for.
 */
async function readTauMax(profileUrl: string): Promise<number> {
  const url = profileUrl.replace(/\.u16$/, '.meta.json');
  const res = await fetch(url);
  if (!res.ok) return RING_TAU_MAX_FALLBACK;
  let meta: unknown;
  try {
    meta = await res.json();
  } catch {
    // A dev server or a CDN answering a missing path with its index page is
    // the no-sidecar case wearing a 200.
    console.warn(`[atlas] ${url} is not JSON; decoding at tau_max ${RING_TAU_MAX_FALLBACK}`);
    return RING_TAU_MAX_FALLBACK;
  }
  const enc = (meta as { tau_encoding?: { tau_max?: unknown } } | null)?.tau_encoding;
  const tauMax = enc?.tau_max;
  if (typeof tauMax !== 'number' || !Number.isFinite(tauMax) || tauMax <= 0) {
    throw new Error(`${url}: tau_encoding.tau_max is not a positive number (got ${String(tauMax)})`);
  }
  return tauMax;
}

/**
 * A GLSL float literal: a decimal point and an exponent, whatever the value.
 *
 * A component tau of 1e-8 written as `1e-8` is legal GLSL, but a radius of
 * 129100 written as `129100` is an INT, and an int where a float belongs is a
 * compile error on a strict driver rather than a silent coercion.
 */
function glslFloat(x: number): string {
  if (!Number.isFinite(x)) throw new Error(`ring shader: ${x} is not a finite number`);
  return x.toExponential(9);
}

/**
 * `tau` at a ring radius, shared by the annulus and by the planet's own shader.
 *
 * GENERATED per body, because a ring system is a LIST of components and their
 * count, radii and depths are registry data. Each component contributes over
 * its own span and the contributions are SUMMED, which is what a superposition
 * is: Jupiter's Amalthea and Thebe gossamer rings share 129,100 to 181,350 km
 * and a pixel there looks through both, so partitioning them at a boundary
 * neither paper draws would be a shape nobody measured. Summing is also what
 * lets the shadow injected into the body's own material keep sharing these
 * uniforms by reference - it needs one tau along the ray, not a list.
 *
 * One texture per profile-backed component. A profile is up to 73,320 texels
 * and no GPU takes that as a texture width, so it is reshaped into rows and
 * addressed by radius here; the fractional column is handed to the sampler so
 * the hardware interpolates along radius, and the row is pinned to its own
 * texel centre so nothing interpolates ACROSS a row, which leaves one wrong
 * neighbour at each row end.
 *
 * The width, the height and the texel count are LITERALS rather than uniforms:
 * they follow from the component's own radii, which are known when the source
 * is generated, and a uniform holding a constant is one more thing that can be
 * set to another body's value.
 *
 * `uRingCompScale` is `--rings --inject ring-bright`: one component's own
 * depth multiplied, which is how a reader that sees nothing at a published
 * 1e-8 is shown to see anything at all.
 */
function ringTauGlsl(
  components: readonly RingComponent[], profiles: readonly (RingProfileHandle | null)[],
): string {
  const decls: string[] = [];
  const body: string[] = [];
  components.forEach((c, i) => {
    const innerKm = glslFloat(c.inner_m / 1000);
    const outerKm = glslFloat(c.outer_m / 1000);
    const p = profiles[i] ?? null;
    if (p) {
      decls.push(`uniform sampler2D uRingTau${i};`);
      body.push(`  if (km >= ${innerKm} && km <= ${outerKm}) {`
        + `\n    float i${i} = clamp(km - ${innerKm}, 0.0, ${glslFloat(p.texels - 1)});`
        + `\n    float w${i} = floor(i${i} / ${glslFloat(p.width)});`
        + `\n    float c${i} = i${i} - w${i} * ${glslFloat(p.width)};`
        + `\n    tau += uRingCompScale[${i}] * texture2D(uRingTau${i}, vec2(`
        + `(c${i} + 0.5) / ${glslFloat(p.width)}, (w${i} + 0.5) / ${glslFloat(p.height)})).r;`
        + '\n  }');
    } else {
      body.push(`  if (km >= ${innerKm} && km <= ${outerKm}) `
        + `tau += uRingCompScale[${i}] * ${glslFloat(c.tau_constant ?? 0)};`);
    }
  });
  return /* glsl */ `
uniform float uRingFlatTau;
uniform vec2 uRingRadiiM;
uniform vec3 uRingSunDir;
uniform float uRingCompScale[${components.length}];
${decls.join('\n')}

float ringTauAt(float radiusM) {
  if (uRingFlatTau >= 0.0) return uRingFlatTau;
  float km = radiusM * 0.001;
  float tau = 0.0;
${body.join('\n')}
  return tau;
}
`;
}

const RING_VERT = /* glsl */ `
varying vec3 vRingLocal;

#include <common>
#include <logdepthbuf_pars_vertex>

void main() {
  vRingLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  #include <logdepthbuf_vertex>
}
`;

const RING_FRAG = /* glsl */ `
precision highp float;

const float RING_MU_VIEW_FLOOR = ${RING_MU_VIEW_FLOOR.toFixed(6)};
const float RING_MU_SUN_FLOOR = ${RING_MU_SUN_FLOOR.toFixed(6)};
const int RING_FOOTPRINT_TAPS = ${RING_FOOTPRINT_TAPS};

varying vec3 vRingLocal;

/** Body-fixed, local metres: the camera, and the body's own drawn radius. */
uniform vec3 uRingCamPos;
uniform float uRingBodyRadiusM;
/**
 * The point light's own irradiance over 4 pi.
 *
 * Over 4 PI and not over pi: the slab single-scattering integral below carries
 * w0/(4 pi) with the phase normalised so an isotropic scatterer reads 1, and
 * dropping the 4 drew the annulus four times brighter than the Lambert scale the
 * body sphere is lit on - which is what saturated the A ring at tau 0.74.
 */
uniform float uRingSunRadiance;
uniform vec3 uRingSunTint;
uniform float uRingAlbedo;
/**
 * The declared display stretch, on the reflected term ALONE.
 *
 * Never on alpha: the ring takes exactly the measured amount of light out of
 * the planet and the stars behind it, and only what it reflects is lifted. The
 * factor comes off the registry (rings.brightness_gain, derived by
 * tools/atlas/bake_rings.py) and is printed on the body card and in the
 * honesty overlay wherever it is not 1.
 */
uniform float uRingGain;
/** Back then forward: which one applies is decided per fragment. */
uniform vec2 uRingPhaseG;
/** The sub-pixel fade, on both radiance and alpha - see setFade. */
uniform float uRingFade;
/** --rings --inject point-sample: the pre-footprint three-tap read back. */
uniform float uRingPointSample;
/**
 * --rings --rings-body jupiter: the same pose with the annulus drawn empty.
 *
 * A discard and not mesh.visible = false: the arm projects a ring radius
 * through the frame's own camera and drops a sample the annulus is not drawn
 * on, so hiding the mesh would take away the samples along with the light.
 * What the two frames then differ by at a radius is that radius's own ring
 * light and nothing else.
 */
uniform float uRingSuppress;

#include <common>
#include <logdepthbuf_pars_fragment>

/** Henyey-Greenstein over 4 pi, so an isotropic scatterer reads 1. */
float ringPhase(float g, float cosT) {
  float d = max(1e-4, 1.0 + g * g - 2.0 * g * cosT);
  return (1.0 - g * g) / (d * sqrt(d));
}

/**
 * Single scattering off a homogeneous slab of normal optical depth tau.
 *
 * The lit face saturates with tau, so the B ring is bright there and the
 * Cassini Division is not; the unlit face is brightest where tau is about the
 * view's own cosine, so the same Division and the C ring are what shows
 * through and the B ring goes dark.
 */
float ringGeom(float tau, float mus, float muv, bool sameSide) {
  if (sameSide) {
    return (mus / (mus + muv)) * (1.0 - exp(-tau * (1.0 / mus + 1.0 / muv)));
  }
  float k = 1.0 / mus - 1.0 / muv;
  float integral = abs(k) < 1e-4 ? tau : (1.0 - exp(-tau * k)) / k;
  return (1.0 / muv) * exp(-tau / muv) * integral;
}

void main() {
  float r = length(vRingLocal.xz);
  if (r < uRingRadiiM.x || r > uRingRadiiM.y) discard;
  if (uRingSuppress > 0.5) discard;
  #include <logdepthbuf_fragment>

  vec3 view = uRingCamPos - vRingLocal;
  float viewLen = length(view);
  if (viewLen < 1e-3) discard;
  view /= viewLen;
  // Floors, not guards against a divide - see RING_MU_VIEW_FLOOR.
  float muv = max(abs(view.y), RING_MU_VIEW_FLOOR);
  float mus = max(abs(uRingSunDir.y), RING_MU_SUN_FLOOR);

  // The pixel's own radial footprint. A texel is a kilometre and a pixel is
  // tens to thousands of them, so one fetch aliases the B ring into the
  // Cassini Division and back again as the camera moves.
  float dr = min(clamp(fwidth(r), 0.0, 4.0e6), uRingRadiiM.y - uRingRadiiM.x);
  bool sameSide = uRingSunDir.y * view.y > 0.0;

  // The mean of the TERM over that footprint, not the term of the mean tau.
  // Both the reflected geometry and the extinction alpha are averaged, because
  // the blend is radiance + (1 - alpha) * behind and the mean of that over a
  // pixel is the mean radiance over one minus the mean alpha.
  float geom = 0.0;
  float alpha = 0.0;
  if (uRingPointSample > 0.5) {
    // --rings --inject point-sample: the three taps of a 1 km texel this
    // replaced, so a check can be run against the sampling it was written for.
    float tau = (ringTauAt(r - dr * 0.33) + ringTauAt(r) + ringTauAt(r + dr * 0.33)) / 3.0;
    geom = ringGeom(tau, mus, muv, sameSide);
    alpha = 1.0 - exp(-tau / muv);
  } else {
    // One tap per kilometre of footprint, which is one per profile texel, up
    // to the cap; past it the taps are spread evenly over the footprint.
    int taps = int(clamp(ceil(dr * 0.001), 1.0, float(RING_FOOTPRINT_TAPS)));
    float dstep = dr / float(taps);
    float rFirst = r - 0.5 * dr + 0.5 * dstep;
    for (int j = 0; j < RING_FOOTPRINT_TAPS; j++) {
      if (j >= taps) break;
      float tau = ringTauAt(rFirst + dstep * float(j));
      geom += ringGeom(tau, mus, muv, sameSide);
      alpha += 1.0 - exp(-tau / muv);
    }
    geom /= float(taps);
    alpha /= float(taps);
  }

  // The planet's shadow on the rings: from here toward the sun, against the
  // body's own drawn sphere.
  float pb = dot(vRingLocal, uRingSunDir);
  float pc = dot(vRingLocal, vRingLocal) - uRingBodyRadiusM * uRingBodyRadiusM;
  float lit = (pb < 0.0 && pb * pb - pc > 0.0) ? 0.0 : 1.0;

  float cosT = dot(-uRingSunDir, view);
  float phase = ringPhase(sameSide ? uRingPhaseG.x : uRingPhaseG.y, cosT);
  vec3 radiance = uRingSunTint * (uRingSunRadiance * uRingAlbedo * uRingGain * phase
    * max(0.0, geom) * lit) * uRingFade;
  alpha = clamp(alpha, 0.0, 1.0) * uRingFade;
  gl_FragColor = vec4(radiance, alpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * The ring shadow on the planet, injected into the body's own material.
 *
 * A ray from the shaded fragment toward the sun against the ring plane, and the
 * profile's transmittance where it lands inside the annulus. It scales the
 * DIRECT terms only: the ambient and the environment do not come from the sun
 * and a ring cannot occlude them.
 *
 * One tap, where the annulus above takes the mean of its term over the pixel's
 * radial footprint. The footprint here is not the annulus's: it is the BODY
 * fragment's own, stretched along the sun direction onto the ring plane, so it
 * would have to be taken from the body's derivatives rather than the ring's,
 * and the term is a transmittance rather than a saturating reflectance. A ring
 * narrower than that footprint therefore still draws its shadow as a hard band
 * on whichever pixel rows the ray happens to land in, at full depth rather
 * than at its covered fraction. Nothing this arm reads is that shadow; it is
 * the next thing to integrate and it is stated here rather than left implied.
 */
const RING_SHADOW_INJECT_GLSL = /* glsl */ `
#include <lights_fragment_end>
{
  float fxRingT = 1.0;
  float fxSy = uRingSunDir.y;
  if (abs(fxSy) > 1e-4) {
    float fxT = -vRingBodyLocal.y / fxSy;
    if (fxT > 0.0) {
      vec3 fxHit = vRingBodyLocal + fxT * uRingSunDir;
      float fxR = length(fxHit.xz);
      if (fxR >= uRingRadiiM.x && fxR <= uRingRadiiM.y) {
        fxRingT = exp(-ringTauAt(fxR) / max(abs(fxSy), 0.005));
      }
    }
  }
  reflectedLight.directDiffuse *= fxRingT;
  reflectedLight.directSpecular *= fxRingT;
}
`;

export interface RingProfileHandle {
  /** Named for `descent-dojo --colour`'s role table: coefficients, so linear. */
  readonly texture: THREE.DataTexture;
  readonly texels: number;
  readonly width: number;
  readonly height: number;
  /** Resolves true once the bake's bytes are in the texture. */
  readonly ready: Promise<boolean>;
  /** The same values on the CPU, so an instrument can read tau without a GPU. */
  tau(): Float32Array | null;
  dispose(): void;
}

/**
 * The baked profile as a texture, and as an array.
 *
 * Allocated at its full size and zero-filled up front, so the annulus can be
 * built and its program linked before the bytes land: tau 0 is transmittance 1,
 * which draws nothing. Half float rather than 16-bit normalised, because R16F
 * is filterable in core WebGL2 and a normalised R16 needs an extension.
 */
export function createRingProfile(
  row: BodyRow, component: RingComponent,
): RingProfileHandle {
  const texels = Math.max(
    1, Math.round((component.outer_m - component.inner_m) / 1000 / RING_KM_PER_TEXEL));
  const width = Math.min(texels, RING_TEX_MAX_WIDTH);
  const height = Math.ceil(texels / width);
  const data = new Uint16Array(width * height);
  const texture = new THREE.DataTexture(
    data, width, height, THREE.RedFormat, THREE.HalfFloatType);
  texture.name = `ring-tau:${row.id}:${component.name}`;
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  let cpu: Float32Array | null = null;
  let disposed = false;
  // The registry records the bake's own repo path here, not a URL the way
  // `row.texture` does.
  const url = `/${component.profile!.replace(/^public\//, '')}`;
  const ready = (async (): Promise<boolean> => {
    // Together: the sidecar is 4 KB against the profile's 147 and its value is
    // only needed to decode, so serialising them would buy nothing.
    const [tauMax, res] = await Promise.all([readTauMax(url), fetch(url)]);
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
    const raw = new Uint16Array(await res.arrayBuffer());
    if (disposed) return false;
    if (raw.length !== texels) {
      throw new Error(
        `${url}: ${raw.length} texels, but ${row.id}'s ${component.name} component spans `
        + `${texels} km`);
    }
    const tau = new Float32Array(texels);
    for (let i = 0; i < texels; i++) {
      const v = (raw[i]! / 65535) * tauMax;
      tau[i] = v;
      data[i] = THREE.DataUtils.toHalfFloat(v);
    }
    cpu = tau;
    texture.needsUpdate = true;
    return true;
  })();
  return {
    texture,
    texels,
    width,
    height,
    ready,
    tau: () => cpu,
    dispose(): void {
      disposed = true;
      texture.dispose();
      cpu = null;
    },
  };
}

export interface RingsConfig {
  row: BodyRow;
  /**
   * One entry per `rings.components` entry, in order.
   *
   * Null where that component carries a `tau_constant` and needs no texture.
   * Positional rather than keyed by name, because the generated source
   * addresses a component by its index and a list that has slipped by one
   * would draw one component's depth over another's radii.
   */
  profiles: readonly (RingProfileHandle | null)[];
  /** The point light's own intensity, so the rings and the body share a scale. */
  sunIntensity: number;
  sunColor: THREE.Color;
}

/** One component of the drawn annulus, as an instrument reads it back. */
export interface RingComponentState {
  readonly name: string;
  readonly innerKm: number;
  readonly outerKm: number;
  /** Null on a profile-backed component: its depth is a texture, not a number. */
  readonly tauConstant: number | null;
  readonly profile: string | null;
  /** What `--inject ring-bright` has this component's depth multiplied by. */
  readonly scale: number;
}

export interface RingsHandle {
  readonly mesh: THREE.Mesh;
  /** Shared by reference with the body's own material - see `applyRingShadow`. */
  readonly uniforms: Record<string, THREE.IUniform>;
  readonly innerKm: number;
  readonly outerKm: number;
  /** Normal optical depth at a ring radius, off the CPU copy of the profile. */
  tauAt(radiusKm: number): number;
  /** A point on the ring plane, body-fixed frame, metres. */
  pointAt(radiusKm: number, azimuthDeg: number, out: THREE.Vector3): THREE.Vector3;
  /**
   * @param sunDirBodyFrame unit, toward the sun, in the body-fixed frame.
   * @param cameraPosBodyFrame the camera relative to the body centre, in that
   *   same frame and in the LOCAL metres the geometry is in - so divided by
   *   whatever scale the orrery drew the body at.
   */
  update(sunDirBodyFrame: THREE.Vector3, cameraPosBodyFrame: THREE.Vector3): void;
  /**
   * How much of the annulus is drawn, 0 to 1, on radiance and alpha together.
   *
   * The sub-pixel rule below is a hard gate on the TRUE angular size, and what
   * is DRAWN at the moment it trips is the ring of a body held at the orrery's
   * own angular floor - several pixels wide, not the fraction of one the true
   * size says. Toggling `visible` there is a pop; the caller ramps this to 0
   * over the decade of true size above the gate instead.
   */
  setFade(fade: number): void;
  /** The fade the annulus is currently drawn at: it scales every grey level an
   *  instrument reads off the frame, so `--rings` gates on it being 1. */
  readonly fade: number;
  /** `descent-dojo --rings`: one tau everywhere, so structure cannot be read. */
  setFlatTau(tau: number | null): void;
  /** `descent-dojo --rings --inject ice-albedo`: another particle albedo, or
   *  null for the registry's own. */
  setAlbedo(albedo: number | null): void;
  /** `descent-dojo --rings --inject point-sample`: the three-tap read of the
   *  profile this replaced, in place of the footprint mean. */
  setPointSample(on: boolean): void;
  /** `descent-dojo --rings --inject gain-saturn`: another row's brightness
   *  factor, or null for this row's own. */
  setGain(factor: number | null): void;
  /** The stretch the annulus is drawn with this frame. */
  readonly gain: number;
  /** Whether the annulus is drawn on the three-tap read this frame. */
  readonly pointSample: boolean;
  /** The single-scattering albedo the annulus is drawn with this frame. */
  readonly albedo: number;
  /**
   * `descent-dojo --rings --rings-body jupiter`: the annulus drawn empty.
   *
   * The same pose twice, with and without the ring light, is the only reading
   * a system of order 1e-6 has: at the engine's exposure such a component is
   * about 1e-4 grey levels, so there is no brightness STEP to read and what
   * can be read is an upper bound on the difference.
   */
  setSuppressed(on: boolean): void;
  readonly suppressed: boolean;
  /**
   * `descent-dojo --rings --inject ring-bright`: one component's depth times a
   * factor, or every component back to 1.
   */
  setComponentScale(index: number | null, factor: number): void;
  /** The components the annulus is generated from, as drawn this frame. */
  readonly components: readonly RingComponentState[];
  /** The generated `ringTauAt` source the annulus and the shadow both run. */
  readonly tauSource: string;
  dispose(): void;
}

export function createRings(config: RingsConfig): RingsHandle {
  const { row, profiles } = config;
  const rings = row.rings!;
  const components = rings.components;
  if (profiles.length !== components.length) {
    throw new Error(`${row.id}: ${profiles.length} ring profiles against `
      + `${components.length} components, and they are paired by position`);
  }
  const tauSource = ringTauGlsl(components, profiles);
  const scales = components.map(() => 1);
  const uniforms: Record<string, THREE.IUniform> = {
    uRingCompScale: { value: scales },
    uRingFlatTau: { value: -1 },
    uRingRadiiM: { value: new THREE.Vector2(rings.inner_m, rings.outer_m) },
    uRingSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uRingCamPos: { value: new THREE.Vector3(0, 1, 0) },
    uRingBodyRadiusM: { value: row.radius_m },
    uRingSunRadiance: { value: config.sunIntensity / (4 * Math.PI) },
    uRingSunTint: { value: config.sunColor.clone() },
    uRingAlbedo: { value: rings.particle_albedo },
    uRingGain: { value: rings.brightness_gain.factor },
    uRingPhaseG: { value: new THREE.Vector2(RING_G_BACK, RING_G_FORWARD) },
    uRingFade: { value: 1 },
    uRingPointSample: { value: 0 },
    uRingSuppress: { value: 0 },
  };
  profiles.forEach((p, i) => {
    if (p) uniforms[`uRingTau${i}`] = { value: p.texture };
  });

  const geometry = new THREE.RingGeometry(
    rings.inner_m, rings.outer_m, RING_THETA_SEGMENTS, 1);
  // Into the body's equatorial plane: the parent's pole quaternion puts +y on
  // the pole, and `RingGeometry` is built in xy.
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: RING_VERT,
    fragmentShader: `${tauSource}\n${RING_FRAG}`,
    side: THREE.DoubleSide,
    transparent: true,
    // Not a surface: alpha is the slab's own extinction, so the blend is
    // `radiance + transmittance * behind` and the stars come through a gap.
    // No depth write for the same reason - the planet behind it must still win
    // the depth test against anything in front of the ring.
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
  });
  material.name = `rings:${row.id}`;
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `${row.id}-rings`;
  // The parent is drawn at a metre-scale distance and written after the cull.
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;

  const innerKm = rings.inner_m / 1000;
  const outerKm = rings.outer_m / 1000;

  return {
    mesh,
    uniforms,
    innerKm,
    outerKm,
    // The generated `ringTauAt` on the CPU: the same components, the same
    // spans, the same sum, so an instrument reads the tau the shader ran on
    // rather than a second reading of the registry.
    tauAt(radiusKm: number): number {
      const flat = uniforms['uRingFlatTau']!.value as number;
      if (flat >= 0) return flat;
      let tau = 0;
      components.forEach((c, i) => {
        const lo = c.inner_m / 1000;
        const hi = c.outer_m / 1000;
        if (radiusKm < lo || radiusKm > hi) return;
        const cpu = profiles[i]?.tau();
        if (!cpu) { tau += scales[i]! * (c.tau_constant ?? 0); return; }
        // Clamped, not zeroed, inside the component: `ringTauAt` in the shader
        // clamps to the end texels, and an instrument comparing this against
        // the frame needs the same answer at a component's own edges.
        const j = Math.min(Math.max(
          Math.round((radiusKm - lo) / RING_KM_PER_TEXEL), 0), cpu.length - 1);
        tau += scales[i]! * cpu[j]!;
      });
      return tau;
    },
    pointAt(radiusKm: number, azimuthDeg: number, out: THREE.Vector3): THREE.Vector3 {
      const a = (azimuthDeg * Math.PI) / 180;
      const r = radiusKm * 1000;
      return out.set(r * Math.cos(a), 0, r * Math.sin(a));
    },
    update(sunDirBodyFrame: THREE.Vector3, cameraPosBodyFrame: THREE.Vector3): void {
      (uniforms['uRingSunDir']!.value as THREE.Vector3).copy(sunDirBodyFrame).normalize();
      (uniforms['uRingCamPos']!.value as THREE.Vector3).copy(cameraPosBodyFrame);
    },
    setFade(fade: number): void {
      uniforms['uRingFade']!.value = Math.min(Math.max(fade, 0), 1);
    },
    get fade(): number { return uniforms['uRingFade']!.value as number; },
    setFlatTau(tau: number | null): void {
      uniforms['uRingFlatTau']!.value = tau === null ? -1 : tau;
    },
    setAlbedo(albedo: number | null): void {
      uniforms['uRingAlbedo']!.value = albedo === null ? rings.particle_albedo : albedo;
    },
    get albedo(): number { return uniforms['uRingAlbedo']!.value as number; },
    setPointSample(on: boolean): void {
      uniforms['uRingPointSample']!.value = on ? 1 : 0;
    },
    get pointSample(): boolean { return uniforms['uRingPointSample']!.value === 1; },
    setGain(factor: number | null): void {
      uniforms['uRingGain']!.value = factor === null ? rings.brightness_gain.factor : factor;
    },
    get gain(): number { return uniforms['uRingGain']!.value as number; },
    setSuppressed(on: boolean): void {
      uniforms['uRingSuppress']!.value = on ? 1 : 0;
    },
    get suppressed(): boolean { return uniforms['uRingSuppress']!.value === 1; },
    setComponentScale(index: number | null, factor: number): void {
      if (index === null) {
        for (let i = 0; i < scales.length; i++) scales[i] = 1;
        return;
      }
      if (index < 0 || index >= scales.length) return;
      scales[index] = factor;
    },
    get components(): readonly RingComponentState[] {
      return components.map((c, i) => ({
        name: c.name,
        innerKm: c.inner_m / 1000,
        outerKm: c.outer_m / 1000,
        tauConstant: c.tau_constant ?? null,
        profile: c.profile ?? null,
        scale: scales[i]!,
      }));
    },
    tauSource,
    dispose(): void {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
    },
  };
}

/**
 * Put the ring shadow on the body's own material.
 *
 * A distinct program cache key, or this material silently shares a ringless
 * body's: three's own key does not vary with `onBeforeCompile`'s content. PER
 * BODY, because `ringTauAt` is now generated from that body's own component
 * list - one key for every ringed body would give Saturn's compiled program to
 * Jupiter and draw Jupiter's shadow off Saturn's profile.
 *
 * The uniforms are the ring's OWN objects, shared by reference, so one
 * `rings.update` moves the shadow and the annulus together, and the summed
 * per-component tau is what makes one shared `ringTauAt` enough for both.
 */
export function applyRingShadow(
  material: THREE.MeshStandardMaterial, rings: RingsHandle, bodyId: string,
): void {
  const shadow = bodyShadowRefOf(material);
  material.onBeforeCompile = (shader): void => {
    Object.assign(shader.uniforms, rings.uniforms);
    shader.vertexShader = `varying vec3 vRingBodyLocal;\n${shader.vertexShader}`
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\n  vRingBodyLocal = position;');
    shader.fragmentShader = `varying vec3 vRingBodyLocal;\n${rings.tauSource}\n${shader.fragmentShader}`
      .replace('#include <lights_fragment_end>', RING_SHADOW_INJECT_GLSL);
    if (shadow) applyBodyShadow(shader, shadow);
  };
  material.customProgramCacheKey = (): string => `fxciv-ring-shadow:${bodyId}${bodyShadowKey(shadow)}`;
}
